/**
 * W65 — /opportunity/:id — Opportunity Detail Page
 *
 * Two-column layout (60/40 desktop, single column at 768px and below).
 * Left: Pricing Lines + Forecast & Override + Sales Plan Tie-in.
 * Right: Credit Splits + CP Progress + Activity Log.
 * Bottom band: Close/status actions.
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, useLocation, Link } from "wouter";
import { getSession } from "@/lib/session";
import { Header, Footer } from "@/components/Shell";
import { OpportunityHeader } from "@/components/opportunities/OpportunityHeader";
import { OpportunityLines } from "@/components/opportunities/OpportunityLines";
import { ForecastOverridePanel } from "@/components/opportunities/ForecastOverridePanel";
import { CreditSplitsPanel } from "@/components/opportunities/CreditSplitsPanel";
import { SalesPlanTieIn } from "@/components/opportunities/SalesPlanTieIn";
import { OpportunityActivityLog } from "@/components/opportunities/OpportunityActivityLog";

// 5-color UI palette (canon)
const CP_COLORS = ["#6B7280", "#DC2626", "#EA580C", "#EAB308", "#16A34A"];

function cpDotColor(score: number): string {
  if (score >= 80) return CP_COLORS[4];
  if (score >= 65) return CP_COLORS[3];
  if (score >= 50) return CP_COLORS[2];
  if (score >= 30) return CP_COLORS[1];
  return CP_COLORS[0];
}

const CP_NAMES = [
  "Discovery", "Qualification", "Solution Fit", "Champion", "Business Case",
  "Decision Process", "Economic Buyer", "Technical Validation", "Negotiate", "Close",
];

interface CloseModalProps {
  opportunityId: string;
  onClose: () => void;
  onClosed: () => void;
}

function CloseModal({ opportunityId, onClose, onClosed }: CloseModalProps) {
  const [status, setStatus] = useState("won");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const session = getSession();
  const token = session ? (session as any).access_token ?? (session as any).token ?? "" : "";
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  async function handleClose() {
    if (!reason.trim()) { setError("Close reason required."); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/opportunities/${opportunityId}/close`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ status, close_reason: reason.trim() }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error ?? "Failed to close");
      }
      onClosed();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: "#FFFFFF", borderRadius: 10, padding: 24, width: "100%", maxWidth: 420,
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        }}
      >
        <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700 }}>Close Opportunity</h3>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", display: "block", marginBottom: 4 }}>STATUS</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{ width: "100%", border: "1px solid #C9C9CC", borderRadius: 4, padding: "7px 8px", fontSize: 13 }}
          >
            <option value="won">Won</option>
            <option value="lost">Lost</option>
            <option value="disqualified">Disqualified</option>
            <option value="on_hold">On Hold</option>
          </select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", display: "block", marginBottom: 4 }}>CLOSE REASON <span style={{ color: "#DC2626" }}>*</span></label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Briefly explain the outcome…"
            style={{ width: "100%", boxSizing: "border-box", border: "1px solid #C9C9CC", borderRadius: 4, padding: "6px 8px", fontSize: 13, minHeight: 64, fontFamily: "inherit", resize: "vertical" }}
          />
        </div>
        {error && <div style={{ background: "#FEE2E2", color: "#991B1B", fontSize: 12, padding: "6px 10px", borderRadius: 4, marginBottom: 10 }}>{error}</div>}
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={handleClose}
            disabled={saving}
            style={{ background: "#0B0B0F", color: "#FFFFFF", border: "none", borderRadius: 5, padding: "8px 18px", fontSize: 13, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", flex: 1 }}
          >
            {saving ? "Saving…" : "Confirm"}
          </button>
          <button
            onClick={onClose}
            style={{ background: "none", border: "1px solid #C9C9CC", borderRadius: 5, padding: "8px 14px", fontSize: 13, cursor: "pointer", color: "#525258" }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OpportunityDetail() {
  const { id } = useParams<{ id: string }>();
  const [, setLoc] = useLocation();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCloseModal, setShowCloseModal] = useState(false);

  const session = getSession();
  const token = session ? (session as any).access_token ?? (session as any).token ?? "" : "";
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/opportunities/${id}`, { headers: authHeader });
      if (!res.ok) throw new Error("Failed to load opportunity");
      const j = await res.json();
      setData(j);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#FAFAF7" }}>
        <Header />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200 }}>
          <span style={{ fontSize: 13, color: "#8E8E94" }}>Loading opportunity…</span>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ minHeight: "100vh", background: "#FAFAF7" }}>
        <Header />
        <div style={{ maxWidth: 600, margin: "40px auto", padding: 24 }}>
          <div style={{ background: "#FEE2E2", color: "#991B1B", padding: 16, borderRadius: 8, fontSize: 13 }}>
            {error ?? "Opportunity not found."}
          </div>
          <Link href="/opportunities" style={{ display: "block", marginTop: 12, fontSize: 13, color: "#0B0B0F" }}>
            ← Back to Opportunities
          </Link>
        </div>
      </div>
    );
  }

  const { opportunity: opp, lines, credits, activity } = data;
  const cpCurrent = Number(opp.current_checkpoint ?? 1);
  const score = Number(opp.composite_score_current ?? 0);

  // If won/lost/disqualified, redirect to list after 2s
  function handleClosed() {
    setShowCloseModal(false);
    loadData();
  }

  return (
    <div style={{ minHeight: "100vh", background: "#FAFAF7", fontFamily: "Inter, system-ui, sans-serif" }}>
      <Header />

      {/* Top band */}
      <OpportunityHeader opportunity={opp} />

      {/* Main body */}
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "20px 16px 32px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0,3fr) minmax(0,2fr)",
            gap: 20,
          }}
          className="opp-grid"
        >
          {/* LEFT column */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Pricing Lines */}
            <OpportunityLines
              opportunityId={opp.opportunity_id ?? opp.id}
              lines={lines}
              subtotalDiscountPct={Number(opp.subtotal_discount_pct ?? 0)}
              onLinesChanged={loadData}
            />

            {/* Forecast & Override */}
            <ForecastOverridePanel
              opportunityId={opp.opportunity_id ?? opp.id}
              compositeScore={score}
              forecastBucket={opp.forecast_bucket ?? "Omit"}
              overrideActive={!!opp.override_active}
              overrideDefense={opp.override_defense}
              overrideSetAt={opp.override_set_at}
              overrideSetByName={opp.override_set_by_name}
              forecastCategoryOverride={opp.forecast_category_override}
              onSaved={loadData}
            />

            {/* Sales Plan Tie-in */}
            <SalesPlanTieIn
              ownerUserId={opp.primary_owner_user_id}
              ownerName={opp.primary_owner_name}
              estCloseDate={opp.est_close_date}
            />
          </div>

          {/* RIGHT column */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Credit Splits */}
            <CreditSplitsPanel
              opportunityId={opp.opportunity_id ?? opp.id}
              credits={credits}
              onSaved={loadData}
            />

            {/* CP Progress strip */}
            <div
              style={{
                background: "#FFFFFF",
                borderRadius: 8,
                border: "1px solid #E5E5E7",
                padding: "12px 16px",
              }}
            >
              <h3 style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 700, color: "#0B0B0F" }}>
                Checkpoint Progress
              </h3>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {CP_NAMES.map((name, i) => {
                  const cpNum = i + 1;
                  const isActive = cpNum === cpCurrent;
                  const isPast = cpNum < cpCurrent;
                  const dotColor = isPast ? cpDotColor(score) : isActive ? cpDotColor(score) : CP_COLORS[0];
                  return (
                    <div
                      key={cpNum}
                      title={`CP${cpNum} — ${name}`}
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 6,
                        background: isPast || isActive ? dotColor : "#EFEFEE",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 10,
                        fontWeight: 700,
                        color: isPast || isActive ? "#FFFFFF" : "#C9C9CC",
                        border: isActive ? `2px solid ${dotColor}` : "2px solid transparent",
                        boxSizing: "border-box",
                        flexShrink: 0,
                        cursor: "default",
                      }}
                    >
                      {cpNum}
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 8, fontSize: 11, color: "#6B7280" }}>
                Current: CP{cpCurrent} — {CP_NAMES[cpCurrent - 1] ?? ""}
              </div>
            </div>

            {/* Activity Log */}
            <OpportunityActivityLog events={activity ?? []} />
          </div>
        </div>

        {/* Bottom band — actions */}
        {opp.status === "open" && (
          <div
            style={{
              marginTop: 24,
              padding: "14px 0",
              borderTop: "1px solid #E5E5E7",
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={() => setShowCloseModal(true)}
              style={{ background: "#16A34A", color: "#FFFFFF", border: "none", borderRadius: 6, padding: "9px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            >
              Mark Won
            </button>
            <button
              onClick={() => setShowCloseModal(true)}
              style={{ background: "#DC2626", color: "#FFFFFF", border: "none", borderRadius: 6, padding: "9px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            >
              Mark Lost
            </button>
            <button
              onClick={() => setShowCloseModal(true)}
              style={{ background: "#EA580C", color: "#FFFFFF", border: "none", borderRadius: 6, padding: "9px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            >
              Mark Disqualified
            </button>
            <button
              onClick={() => setShowCloseModal(true)}
              style={{ background: "#6B7280", color: "#FFFFFF", border: "none", borderRadius: 6, padding: "9px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            >
              Put On Hold
            </button>
          </div>
        )}

        {opp.status !== "open" && (
          <div
            style={{
              marginTop: 16,
              padding: "10px 14px",
              background: "#EFEFEE",
              borderRadius: 7,
              fontSize: 13,
              color: "#525258",
            }}
          >
            This opportunity is closed ({opp.status}).{" "}
            {opp.close_reason && <span>&ldquo;{opp.close_reason}&rdquo;</span>}
            {opp.closed_at && (
              <span style={{ marginLeft: 6, color: "#8E8E94", fontSize: 11 }}>
                {new Date(opp.closed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </span>
            )}
          </div>
        )}
      </div>

      <Footer />

      {/* Close modal */}
      {showCloseModal && (
        <CloseModal
          opportunityId={opp.opportunity_id ?? opp.id}
          onClose={() => setShowCloseModal(false)}
          onClosed={handleClosed}
        />
      )}

      {/* Responsive CSS */}
      <style>{`
        @media (max-width: 768px) {
          .opp-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
